import {
  GEO_SIM_ELIGIBLE_KEY,
  GEO_SIM_SESSION_KEY,
  type GeoSimSession,
  type GeoSimVpnMode,
} from "./constants";

function safeParse(raw: string | null): GeoSimSession | null {
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as Partial<GeoSimSession>;
    if (!o || typeof o !== "object") return null;
    const country = String(o.country || "")
      .trim()
      .toUpperCase();
    if (!/^[A-Z]{2}$/.test(country)) return null;
    const token = typeof o.token === "string" ? o.token.trim() : "";
    if (!token || !token.includes(".")) return null;
    const vpn = (o.simulateVpn || "clean") as GeoSimVpnMode;
    return {
      country,
      token,
      simulateVpn:
        vpn === "anonymous" || vpn === "hosting" || vpn === "clean"
          ? vpn
          : "clean",
      ...(typeof o.state === "string" && o.state.trim()
        ? { state: o.state.trim().toUpperCase() }
        : {}),
      ...(typeof o.province === "string" && o.province.trim()
        ? { province: o.province.trim() }
        : {}),
      ...(typeof o.city === "string" && o.city.trim()
        ? { city: o.city.trim() }
        : {}),
      ...(typeof o.neighborhood === "string" && o.neighborhood.trim()
        ? { neighborhood: o.neighborhood.trim() }
        : {}),
      ...(typeof o.simulateIp === "string" && o.simulateIp.trim()
        ? { simulateIp: o.simulateIp.trim() }
        : {}),
      ...(typeof o.expiresAt === "string" ? { expiresAt: o.expiresAt } : {}),
      ...(typeof o.path === "string" ? { path: o.path } : {}),
    };
  } catch {
    return null;
  }
}

export function readGeoSimSession(): GeoSimSession | null {
  if (typeof window === "undefined") return null;
  try {
    return safeParse(sessionStorage.getItem(GEO_SIM_SESSION_KEY));
  } catch {
    return null;
  }
}

export function writeGeoSimSession(session: GeoSimSession): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(GEO_SIM_SESSION_KEY, JSON.stringify(session));
  } catch {
    /* quota / private mode */
  }
  // Alinear checkout/pricing (resolveCheckoutCountryCode lee test_country).
  // Sin esto, un ?country=MX previo en localStorage pisa el GeoSim AR y el
  // CTA Mux no muestra logo Mercado Pago / gateway MP.
  try {
    if (session.country && /^[A-Z]{2}$/.test(session.country)) {
      localStorage.setItem("test_country", session.country);
    }
  } catch {
    /* ignore */
  }
}

export function clearGeoSimSession(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(GEO_SIM_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Persist across tabs (localStorage). sessionStorage alone failed in prod:
 * "Ver sitio" opens a new tab → eligible lost → Clerk stays deferred on `/`
 * → toolbar never mounts.
 */
export function markGeoSimEligible(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(GEO_SIM_ELIGIBLE_KEY, "1");
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(GEO_SIM_ELIGIBLE_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function isGeoSimEligibleMarked(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (localStorage.getItem(GEO_SIM_ELIGIBLE_KEY) === "1") return true;
  } catch {
    /* ignore */
  }
  try {
    return sessionStorage.getItem(GEO_SIM_ELIGIBLE_KEY) === "1";
  } catch {
    return false;
  }
}

/** true si hay sesión local o el Worker ya inyectó adminGeoSim */
export function hasActiveGeoSimIntent(): boolean {
  if (typeof window === "undefined") return false;
  if (readGeoSimSession()) return true;
  try {
    const cfg = (
      window as unknown as {
        __TEMPLATE_CONFIG__?: { adminGeoSim?: unknown };
      }
    ).__TEMPLATE_CONFIG__;
    if (cfg?.adminGeoSim && typeof cfg.adminGeoSim === "object") return true;
  } catch {
    /* ignore */
  }
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get("geo_sim") === "1" && p.get("gst")) return true;
  } catch {
    /* ignore */
  }
  return false;
}

/** Clerk deferred paths: forzar mount si el operador puede / está simulando */
export function shouldForceClerkForGeoSim(): boolean {
  if (typeof window === "undefined") return false;
  if (isGeoSimEligibleMarked()) return true;
  if (hasActiveGeoSimIntent()) return true;
  return false;
}
